import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button, Empty, Field, Input, Lookup, Modal, Notice, Panel, Select, Table, type ColumnDef,
} from '../design/index.js'
import {
  useApi,
  type DriveCollection,
  type DriveFile,
  type TeamMember,
} from '../app/api.js'
import { navigate, paths } from '../app/router.js'
import { TeamBoard } from '../framework/TeamBoard.js'
import { ProjectTeam } from '../framework/ProjectTeam.js'
import { SchedulePage } from './SchedulePage.js'
import { KickoffPanel } from '../framework/KickoffPanel.js'
import { ErrorBlock, Loading, messageFor } from '../framework/feedback.js'
import { formatDateTime } from '../framework/format.js'
import { DropZone, UploadQueueList, useUploadQueue } from '../framework/UploadQueue.js'

/**
 * Project Drive as a place of its own (docs/05 §2).
 *
 * It is not a report — it is a file store keyed by project, with three views
 * of the same set: everything on the job, one collection at a time, and the
 * files attached to one transaction. The last one is the reason the drive
 * exists at all: "send me the test certificate for GRN-CHK 0412" is a question
 * about a document, asked by somebody holding a delivery note.
 */

const readAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('That file could not be read.'))
    reader.onload = () => {
      const result = String(reader.result ?? '')
      resolve(result.slice(result.indexOf(',') + 1))
    }
    reader.readAsDataURL(file)
  })

const kb = (bytes: number): string =>
  bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`

type Tab = 'files' | 'kickoff' | 'collections' | 'team' | 'board' | 'schedule'

const TABS: ReadonlyArray<{ key: Tab; label: string }> = [
  { key: 'files', label: 'Files' },
  // docs/24 §3.1 — the pack a job starts with, and whether it is complete.
  { key: 'kickoff', label: 'Kick-off' },
  { key: 'collections', label: 'Collections' },
  { key: 'team', label: 'Project team' },
  { key: 'board', label: 'Team board' },
  { key: 'schedule', label: 'Schedule' },
]

/**
 * Project Drive opened with no project (PROJ3-028): the reference's "Select
 * Project" modal over "No transaction reference!". Done opens that
 * project's drive; Cancel leaves the empty page, the picker a click away.
 */
const SelectProjectPrompt = ({ companyId }: { companyId: string }) => {
  const [open, setOpen] = useState(true)
  const [projectId, setProjectId] = useState('')
  return (
    <>
      <Empty
        title="No transaction reference!"
        body={['Please select a transaction no to view its files.']}
        actions={<Button kind="primary" onClick={() => setOpen(true)}>Select project</Button>}
      />
      {open ? (
        <Modal
          title="Select Project"
          onClose={() => setOpen(false)}
          actions={(
            <>
              <Button kind="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button kind="primary" disabled={projectId === ''}
                onClick={() => navigate(paths.drive(companyId, { project: projectId }))}>
                Done
              </Button>
            </>
          )}
        >
          <Field label="Project" required>
            <Lookup entity="project" value={projectId} onChange={(v) => setProjectId(v)} />
          </Field>
        </Modal>
      ) : null}
    </>
  )
}

export const ProjectDrivePage = ({
  companyId, projectId, projectName,
}: {
  companyId: string
  projectId: string
  projectName?: string
}) => {
  const api = useApi()
  const client = useQueryClient()
  const picker = useRef<HTMLInputElement | null>(null)

  const [tab, setTab] = useState<Tab>('files')
  const [collectionId, setCollectionId] = useState('')
  const [documentFilter, setDocumentFilter] = useState('')
  const [includeTeamBoard, setIncludeTeamBoard] = useState(false)
  const [search, setSearch] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [newCollection, setNewCollection] = useState<{ name: string; typeId: string; description: string } | null>(null)
  const [categoryId, setCategoryId] = useState('')
  const [details, setDetails] = useState<{
    file: DriveFile; fileCategoryId: string; revision: string; approvalState: string; itemId: string; itemLabel: string
  } | null>(null)

  const filesKey = ['drive', 'files', api.companyId, projectId, collectionId, categoryId, documentFilter, includeTeamBoard, search]
  const files = useQuery({
    queryKey: filesKey,
    queryFn: () => api.driveFiles({
      projectId,
      ...(collectionId ? { collectionId } : {}),
      ...(categoryId ? { fileCategoryId: categoryId } : {}),
      ...(documentFilter ? { documentId: documentFilter } : {}),
      ...(search ? { q: search } : {}),
      includeTeamBoard,
    }),
    enabled: projectId !== '',
  })

  const collections = useQuery({
    queryKey: ['drive', 'collections', api.companyId, projectId],
    queryFn: () => api.collections(projectId),
    enabled: projectId !== '',
  })

  const fileCategories = useQuery({
    queryKey: ['master-list', api.companyId, 'file_category'],
    queryFn: () => api.masterList('file_category', { pageSize: '100' }),
  })
  const categoryOptions = (fileCategories.data?.rows ?? []).map((r) => ({
    value: String(r['id']), label: String(r['name'] ?? ''),
  }))

  const collectionTypes = useQuery({
    queryKey: ['master-list', api.companyId, 'collection_type'],
    queryFn: () => api.masterList('collection_type', { pageSize: '100' }),
  })

  const invalidate = () => {
    void client.invalidateQueries({ queryKey: ['drive'] })
    // A file filed or removed can change whether the job is ready to buy for.
    void client.invalidateQueries({ queryKey: ['kickoff'] })
  }

  // The server refuses past 25 MB (drive-service.ts); saying so before the
  // upload saves sending the bytes to be told.
  const queue = useUploadQueue({ maxBytes: 25 * 1024 * 1024, onUploaded: invalidate })

  /**
   * Every file picked or dropped together is filed under the collection and
   * category showing at that moment — one choice for the batch.
   */
  const uploadAll = (picked: readonly File[]): void => {
    setError(null)
    const into = { collectionId, categoryId }
    queue.add(picked, async (file) => api.driveUploadTo({
      fileName: file.name,
      contentType: file.type,
      base64: await readAsBase64(file),
      projectId,
      ...(into.collectionId ? { collectionId: into.collectionId } : {}),
      // Uploaded while one category is showing, it is filed under it.
      ...(into.categoryId ? { fileCategoryId: into.categoryId } : {}),
    }))
  }

  const createCollection = useMutation({
    mutationFn: async () => api.createCollection({
      projectId,
      name: newCollection?.name ?? '',
      ...(newCollection?.typeId ? { collectionTypeId: newCollection.typeId } : {}),
      ...(newCollection?.description.trim() ? { description: newCollection.description.trim() } : {}),
    }),
    onSuccess: () => { setError(null); setNewCollection(null); invalidate() },
    onError: (err) => setError(messageFor(err)),
  })

  const removeCollection = useMutation({
    mutationFn: (id: string) => api.removeCollection(projectId, id),
    onSuccess: () => { setError(null); invalidate() },
    onError: (err) => setError(messageFor(err)),
  })

  const fileInto = useMutation({
    mutationFn: ({ fileId, into }: { fileId: string; into: string }) =>
      api.fileInto(fileId, { collectionId: into === '' ? null : into }),
    onSuccess: () => { setError(null); invalidate() },
    onError: (err) => setError(messageFor(err)),
  })

  const saveDetails = useMutation({
    mutationFn: async () => {
      if (!details) return
      await api.fileInto(details.file.id, {
        fileCategoryId: details.fileCategoryId === '' ? null : details.fileCategoryId,
        revision: details.revision.trim() === '' ? null : details.revision.trim(),
        approvalState: details.approvalState === '' ? null : details.approvalState as 'FOR_REVIEW' | 'APPROVED' | 'SUPERSEDED',
        itemId: details.itemId === '' ? null : details.itemId,
      })
    },
    onSuccess: () => {
      setError(null)
      setDetails(null)
      invalidate()
    },
    // Shown inside the dialog: the page's own notice sits behind it.
  })
  // A failed save's message belongs to that attempt, not to the next file opened.
  const closeDetails = (): void => { saveDetails.reset(); setDetails(null) }

  const removeFile = useMutation({
    mutationFn: (fileId: string) => api.driveDelete(fileId),
    onSuccess: () => { setError(null); invalidate() },
    onError: (err) => setError(messageFor(err)),
  })

  if (projectId === '') return <SelectProjectPrompt companyId={companyId} />

  const fileColumns: ReadonlyArray<ColumnDef<DriveFile>> = [
    {
      key: 'fileName',
      header: 'File',
      render: (row) => <a href={api.driveFileUrl(row.id)}>{row.fileName}</a>,
    },
    {
      key: 'documentNo',
      header: 'Transaction',
      width: 190,
      render: (row) => (row.documentNo
        ? <span className="mono">{row.documentNo}</span>
        : <span className="helper">Uploaded to the drive</span>),
    },
    {
      // docs/24 §3.1 — what kind of document it is, which revision, and where it stands.
      key: 'fileCategoryName',
      header: 'Category',
      width: 240,
      render: (row) => (
        <>
          {row.fileCategoryName
            ? (
              <span className="chip" style={row.fileCategoryColour ? { color: row.fileCategoryColour } : undefined}>
                <span className="chip__dot" />{row.fileCategoryName}
              </span>
            )
            : <span className="helper">No category</span>}
          <div className="cell-sub">
            {[row.revision ? `Rev ${row.revision}` : null,
              row.approvalState === 'APPROVED' ? 'Approved' : row.approvalState === 'FOR_REVIEW' ? 'For review'
                : row.approvalState === 'SUPERSEDED' ? 'Superseded' : null,
              row.itemLabel].filter(Boolean).join(' · ')}
          </div>
        </>
      ),
    },
    {
      key: 'collectionName',
      header: 'Collection',
      width: 220,
      render: (row) => (
        <Select
          value={row.collectionId ?? ''}
          onChange={(into) => fileInto.mutate({ fileId: row.id, into })}
          placeholder="Not filed"
          options={(collections.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
        />
      ),
    },
    { key: 'byteSize', header: 'Size', align: 'right', width: 100, render: (r) => kb(r.byteSize) },
    { key: 'uploadedByName', header: 'Added by', width: 180, render: (r) => r.uploadedByName ?? '—' },
    { key: 'createdAt', header: 'On', width: 165, render: (r) => formatDateTime(r.createdAt) },
    {
      key: 'remove',
      header: '',
      width: 190,
      render: (row) => (
        <div className="row">
          <Button kind="ghost" onClick={() => { saveDetails.reset(); setDetails({
            file: row,
            fileCategoryId: row.fileCategoryId ?? '',
            revision: row.revision ?? '',
            approvalState: row.approvalState ?? '',
            itemId: row.itemId ?? '',
            itemLabel: row.itemLabel ?? '',
          }) }}>Details</Button>
          <Button kind="ghost" onClick={() => removeFile.mutate(row.id)}>Remove</Button>
        </div>
      ),
    },
  ]

  const collectionColumns: ReadonlyArray<ColumnDef<DriveCollection>> = [
    {
      key: 'name',
      header: 'Collection',
      render: (row) => (
        <button type="button" className="linkish" onClick={() => { setCollectionId(row.id); setTab('files') }}>
          {row.name}
        </button>
      ),
    },
    {
      key: 'typeName',
      header: 'Kind',
      width: 220,
      render: (row) => (row.typeName
        ? (
          <span className="chip" style={row.colour ? { color: row.colour } : undefined}>
            <span className="chip__dot" />{row.typeName}
          </span>
        )
        : '—'),
    },
    { key: 'fileCount', header: 'Files', align: 'right', width: 90 },
    { key: 'createdByName', header: 'Made by', width: 180, render: (r) => r.createdByName ?? '—' },
    { key: 'createdAt', header: 'On', width: 165, render: (r) => formatDateTime(r.createdAt) },
    {
      key: 'remove',
      header: '',
      width: 100,
      render: (row) => (
        <Button kind="ghost" onClick={() => removeCollection.mutate(row.id)}>Retire</Button>
      ),
    },
  ]

  return (
    <>
      {error ? <Notice kind="bad">{error}</Notice> : null}

      <nav className="tabstrip" aria-label="Drive sections">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={t.key === tab ? 'tabstrip__tab tabstrip__tab--active' : 'tabstrip__tab'}
            aria-current={t.key === tab ? 'page' : undefined}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === 'files' ? (
        <Panel
          title={collectionId
            ? `Files in ${(collections.data ?? []).find((c) => c.id === collectionId)?.name ?? 'this collection'}`
            : 'Files'}
          aside={
            <div className="row">
              <input
                ref={picker}
                type="file"
                multiple
                aria-label="Files to upload"
                hidden
                onChange={(event) => {
                  const picked = Array.from(event.currentTarget.files ?? [])
                  event.currentTarget.value = ''
                  uploadAll(picked)
                }}
              />
              <Button kind="primary" onClick={() => picker.current?.click()}>
                {queue.busy ? 'Uploading…' : 'Upload files'}
              </Button>
            </div>
          }
        >
          <div className="drive-toolbar">
            <Field label="Search">
              <Input value={search} onChange={setSearch} placeholder="By file name" />
            </Field>
            <Field label="Category" hint="A batch of uploads goes into the category showing.">
              <Select
                value={categoryId}
                onChange={setCategoryId}
                placeholder="Every category"
                options={categoryOptions}
              />
            </Field>
            <Field label="Collection">
              <Select
                value={collectionId}
                onChange={setCollectionId}
                placeholder="Every file on this project"
                options={(collections.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
              />
            </Field>
            <Field label="Transaction" hint="Only the files attached to one document.">
              <Lookup
                entity="document"
                value={documentFilter}
                onChange={(id) => setDocumentFilter(id)}
                placeholder="Any transaction"
              />
            </Field>
            <label className="column-choice">
              <input
                type="checkbox"
                checked={includeTeamBoard}
                onChange={() => setIncludeTeamBoard((on) => !on)}
              />
              <span>Show files from the team board</span>
            </label>
          </div>

          <UploadQueueList items={queue.items} onClear={queue.clear} />

          {files.isLoading ? <Loading /> : null}
          {files.error ? <ErrorBlock error={files.error} /> : null}
          <DropZone onFiles={uploadAll}>
            <Table
              columns={fileColumns}
              rows={files.data ?? []}
              getKey={(row) => row.id}
              empty={
                <Empty
                  title="No files in this drive"
                  body={["Use 'Upload files' — or drop them here — to add several at once, or attach one to a document; it appears here too."]}
                />
              }
            />
          </DropZone>
        </Panel>
      ) : null}

      {tab === 'collections' ? (
        <Panel
          title="Collections"
          aside={
            <Button kind="primary" onClick={() => setNewCollection({ name: '', typeId: '', description: '' })}>
              Create a collection
            </Button>
          }
        >
          {collections.isLoading ? <Loading /> : null}
          <Table
            columns={collectionColumns}
            rows={collections.data ?? []}
            getKey={(row) => row.id}
            empty={
              <Empty
                title="No collections"
                body={["No collections in this drive. Use 'Create a collection' to make one."]}
              />
            }
          />
        </Panel>
      ) : null}

      {tab === 'kickoff' ? <KickoffPanel projectId={projectId} /> : null}

      {tab === 'team' ? <ProjectTeam projectId={projectId} /> : null}

      {tab === 'board' ? (
        <TeamBoard projectId={projectId} title={`Team board — ${projectName ?? 'this project'}`} />
      ) : null}

      {tab === 'schedule' ? <SchedulePage projectId={projectId} /> : null}

      {newCollection ? (
        <Modal
          title="Create a collection"
          subtitle="A folder for one kind of document on this project."
          actions={
            <>
              <Button kind="ghost" onClick={() => setNewCollection(null)}>Cancel</Button>
              <Button
                kind="primary"
                disabled={newCollection.name.trim() === '' || newCollection.typeId === '' || createCollection.isPending}
                onClick={() => createCollection.mutate()}
              >
                Create it
              </Button>
            </>
          }
          onClose={() => setNewCollection(null)}
        >
          {/* Reference: Type*, Name*, Description. */}
          <Field label="Type" required hint="Configured under Masters → Collection types.">
            <Select
              value={newCollection.typeId}
              onChange={(typeId) => setNewCollection({ ...newCollection, typeId })}
              placeholder="Choose a type"
              options={(collectionTypes.data?.rows ?? []).map((r) => ({
                value: String(r['id']), label: String(r['name'] ?? ''),
              }))}
            />
          </Field>
          <Field label="Name" required>
            <Input
              value={newCollection.name}
              onChange={(name) => setNewCollection({ ...newCollection, name })}
            />
          </Field>
          <Field label="Description">
            <textarea
              className="control"
              style={{ minHeight: 88, resize: 'vertical' }}
              value={newCollection.description}
              onChange={(e) => setNewCollection({ ...newCollection, description: e.currentTarget.value })}
            />
          </Field>
        </Modal>
      ) : null}

      {details ? (
        <Modal
          title={details.file.fileName}
          subtitle="What this file is, which revision, and whether it is approved."
          actions={
            <>
              <Button kind="ghost" onClick={closeDetails}>Cancel</Button>
              <Button kind="primary" disabled={saveDetails.isPending} onClick={() => saveDetails.mutate()}>
                Save
              </Button>
            </>
          }
          onClose={closeDetails}
        >
          <Field label="Category" hint="Drawings, approved drawings, terms, procedures, data sheets…">
            <Select
              value={details.fileCategoryId}
              onChange={(fileCategoryId) => setDetails({ ...details, fileCategoryId })}
              placeholder="No category"
              options={categoryOptions}
            />
          </Field>
          {saveDetails.error ? <Notice kind="bad">{messageFor(saveDetails.error)}</Notice> : null}
          <Field label="Revision" hint="As printed on the drawing: R0, R1, Rev B. Up to 20 characters.">
            <Input
              value={details.revision}
              onChange={(revision) => setDetails({ ...details, revision: revision.slice(0, 20) })}
            />
          </Field>
          <Field label="Status">
            <Select
              value={details.approvalState}
              onChange={(approvalState) => setDetails({ ...details, approvalState })}
              placeholder="Not stated"
              options={[
                { value: 'FOR_REVIEW', label: 'For review' },
                { value: 'APPROVED', label: 'Approved' },
                { value: 'SUPERSEDED', label: 'Superseded — kept for the record' },
              ]}
            />
          </Field>
          <Field label="Material" hint="For a data sheet or a test certificate: the material it is for.">
            <Lookup
              entity="item"
              value={details.itemId}
              label={details.itemLabel || undefined}
              onChange={(itemId, option) => setDetails({ ...details, itemId, itemLabel: option?.label ?? '' })}
              placeholder="Search materials"
            />
          </Field>
        </Modal>
      ) : null}

      {/* Somewhere to go back to when this was opened from a document. */}
      <p className="helper" style={{ marginTop: 24 }}>
        <button type="button" className="linkish" onClick={() => navigate(paths.hub(companyId, 'reports'))}>
          Back to reports
        </button>
      </p>
    </>
  )
}
